import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  Crown, 
  CheckCircle, 
  Star,
  Zap,
  Shield,
  TrendingUp,
  ArrowRight,
  X
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlanCard = ({ plan, currentPlan, onSelectPlan, isPopular = false }) => {
  const isCurrentPlan = currentPlan?.plan_name === plan.name;
  const isFreePlan = plan.price_monthly === 0;
  
  const getBadgeColor = () => {
    if (plan.name.includes('Gratuit')) return 'bg-gray-500';
    if (plan.name.includes('Standard')) return 'bg-blue-500';
    if (plan.name.includes('Premium')) return 'bg-purple-500';
    return 'bg-orange-500';
  };

  const getIcon = () => {
    if (plan.name.includes('Gratuit')) return Shield;
    if (plan.name.includes('Standard')) return Star;
    if (plan.name.includes('Premium')) return Crown;
    return Crown;
  };

  const Icon = getIcon();

  return (
    <Card className={`relative ${isCurrentPlan ? 'ring-2 ring-orange-500' : ''} ${isPopular ? 'border-orange-500 shadow-lg' : ''} hover:shadow-lg transition-all`}>
      {isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <Badge className="bg-orange-500 text-white px-3 py-1">
            Le plus populaire
          </Badge>
        </div>
      )}
      
      {isCurrentPlan && (
        <div className="absolute -top-3 right-4">
          <Badge className="bg-green-500 text-white px-3 py-1">
            Plan actuel
          </Badge>
        </div>
      )}

      <CardHeader className="text-center pb-4">
        <div className="flex justify-center mb-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getBadgeColor()}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
        
        <CardTitle className="text-xl">{plan.name}</CardTitle>
        
        <div className="py-4">
          {isFreePlan ? (
            <div className="text-3xl font-bold text-gray-700">Gratuit</div>
          ) : (
            <div>
              <div className="text-3xl font-bold text-orange-600">
                {plan.price_monthly}€
                <span className="text-base font-normal text-gray-500">/mois</span>
              </div>
              <div className="text-sm text-gray-500 mt-1">
                ou {plan.price_yearly}€/an 
                <span className="text-green-600 ml-1">
                  (2 mois offerts !)
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">
            {plan.commission_rate === 0 ? 'Aucune commission' : `${(plan.commission_rate * 100).toFixed(0)}% de commission`}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {plan.commission_rate === 0 
              ? 'Gardez 100% de vos revenus' 
              : `CETROBON prend ${(plan.commission_rate * 100).toFixed(0)}% sur chaque vente`
            }
          </p>
        </div>
      </CardHeader>

      <CardContent>
        <ul className="space-y-3 mb-6">
          {plan.features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>

        {!isCurrentPlan && (
          <div className="space-y-2">
            {!isFreePlan && (
              <Button 
                className="w-full bg-orange-600 hover:bg-orange-700"
                onClick={() => onSelectPlan(plan, 'yearly')}
              >
                <Crown className="w-4 h-4 mr-2" />
                Choisir Annuel - {plan.price_yearly}€
              </Button>
            )}
            
            <Button 
              variant={isFreePlan ? "default" : "outline"}
              className={`w-full ${isFreePlan ? 'bg-gray-600 hover:bg-gray-700' : ''}`}
              onClick={() => onSelectPlan(plan, isFreePlan ? 'free' : 'monthly')}
            >
              {isFreePlan ? (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Commencer Gratuitement
                </>
              ) : (
                <>
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Mensuel - {plan.price_monthly}€
                </>
              )}
            </Button>
          </div>
        )}

        {isCurrentPlan && !isFreePlan && (
          <div className="space-y-3">
            <Badge className="w-full bg-green-100 text-green-800 px-4 py-2 justify-center">
              <CheckCircle className="w-4 h-4 mr-2" />
              Plan actuel
            </Badge>
            <div className="text-xs text-gray-500 text-center">
              Vous pouvez changer de plan à tout moment en sélectionnant un autre plan ci-dessus
            </div>
          </div>
        )}

        {isCurrentPlan && isFreePlan && (
          <div className="text-center">
            <Badge className="bg-green-100 text-green-800 px-4 py-2">
              <CheckCircle className="w-4 h-4 mr-2" />
              Plan actuel
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ChefSubscriptionPlans = ({ user, onClose }) => {
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoValidation, setPromoValidation] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedBilling, setSelectedBilling] = useState(null);

  useEffect(() => {
    loadPlansAndSubscription();
  }, []);

  const loadPlansAndSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Get available plans (public endpoint)
      const plansResponse = await axios.get(`${API}/subscription-plans`);
      setPlans(plansResponse.data);

      // Get current subscription
      try {
        const subResponse = await axios.get(`${API}/chef/subscription`, { headers });
        setCurrentSubscription(subResponse.data);
      } catch (error) {
        // User has no subscription (free plan)
        setCurrentSubscription({ plan_name: "Plan Gratuit", commission_rate: 0.20 });
      }

    } catch (error) {
      console.error("Error loading subscription data:", error);
    } finally {
      setLoading(false);
    }
  };

  const validatePromoCode = async (code, planId, originalPrice) => {
    if (!code.trim()) {
      setPromoValidation(null);
      return;
    }
    
    setValidatingPromo(true);
    
    try {
      const response = await axios.post(
        `${API}/promo-codes/validate?code=${code}&plan_id=${planId}&original_price=${originalPrice}`
      );
      
      setPromoValidation(response.data);
    } catch (error) {
      console.error("Error validating promo code:", error);
      setPromoValidation({
        is_valid: false,
        message: "Erreur lors de la validation du code promo"
      });
    } finally {
      setValidatingPromo(false);
    }
  };

  const handlePromoCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setPromoCode(code);
    
    // Clear previous validation when code changes
    if (promoValidation) {
      setPromoValidation(null);
    }
  };

  const checkPromoForSelectedPlan = () => {
    if (selectedPlan && promoCode && !validatingPromo) {
      const originalPrice = selectedBilling === 'yearly' 
        ? selectedPlan.price_yearly 
        : selectedPlan.price_monthly;
      validatePromoCode(promoCode, selectedPlan.id, originalPrice);
    }
  };

  const handleSelectPlan = async (plan, billingPeriod) => {
    setSelectedPlan(plan);
    setSelectedBilling(billingPeriod);
    
    if (billingPeriod === 'free') {
      // For free plan, just show confirmation
      alert("Vous utilisez déjà le plan gratuit avec 20% de commission.");
      return;
    }

    // Validate promo code for this plan if one is entered
    if (promoCode) {
      const originalPrice = billingPeriod === 'yearly' ? plan.price_yearly : plan.price_monthly;
      await validatePromoCode(promoCode, plan.id, originalPrice);
    }
    
    // Continue with subscription
    await confirmSubscription(plan, billingPeriod);
  };

  const confirmSubscription = async (plan, billingPeriod) => {
    const originalPrice = billingPeriod === 'yearly' ? plan.price_yearly : plan.price_monthly;
    const finalPrice = promoValidation?.is_valid ? promoValidation.final_price : originalPrice;
    const discount = originalPrice - finalPrice;
    
    let confirmMessage = billingPeriod === 'yearly' 
      ? `Confirmer l'abonnement annuel ${plan.name} ?\n\nPrix original: ${originalPrice}€`
      : `Confirmer l'abonnement mensuel ${plan.name} ?\n\nPrix original: ${originalPrice}€`;
    
    if (promoValidation?.is_valid && discount > 0) {
      confirmMessage += `\nCode promo: ${promoCode}\nRéduction: -${discount.toFixed(2)}€\nPRIX FINAL: ${finalPrice}€`;
    } else {
      confirmMessage += `\nPRIX: ${originalPrice}€`;
    }
    
    if (window.confirm(confirmMessage)) {
      setSubscribing(true);
      
      try {
        const token = localStorage.getItem('token');
        
        await axios.post(`${API}/chef/subscribe`, {
          chef_id: user.id,
          plan_id: plan.id,
          billing_period: billingPeriod,
          promo_code: promoValidation?.is_valid ? promoCode : null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const successMessage = promoValidation?.is_valid && discount > 0
          ? `Félicitations ! Vous êtes maintenant abonné au ${plan.name} !\n\nÉconomies réalisées: ${discount.toFixed(2)}€ grâce au code ${promoCode}`
          : `Félicitations ! Vous êtes maintenant abonné au ${plan.name} !`;
        
        alert(successMessage);
        loadPlansAndSubscription(); // Refresh data
        setPromoCode('');
        setPromoValidation(null);
      } catch (error) {
        console.error("Error subscribing to plan:", error);
        const errorMessage = error.response?.data?.detail || "Erreur lors de l'abonnement. Veuillez réessayer.";
        alert(errorMessage);
      } finally {
        setSubscribing(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p>Chargement des plans d'abonnement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-red-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-2xl font-bold text-orange-600">Plans d'Abonnement</h1>
              <p className="text-sm text-gray-600">Choisissez le plan qui vous convient</p>
            </div>
            <Button variant="outline" onClick={onClose}>
              Retour au Dashboard
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Current subscription info */}
        {currentSubscription && (
          <Card className="mb-8 bg-gradient-to-r from-orange-500 to-red-500 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Votre Plan Actuel</h3>
                  <p className="text-orange-100">
                    {currentSubscription.plan_name} - Commission: {(currentSubscription.commission_rate * 100).toFixed(0)}%
                  </p>
                </div>
                <div className="text-right">
                  <TrendingUp className="w-8 h-8 text-orange-200" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pricing explanation */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Développez votre activité avec CETROBON
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Choisissez le plan qui correspond à vos ambitions. Plus vous grandissez, moins vous payez de commissions !
          </p>
        </div>

        {/* Promo Code Section */}
        <div className="max-w-md mx-auto mb-8">
          <Card className="bg-gradient-to-r from-orange-50 to-red-50 border-orange-200">
            <CardContent className="pt-6">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-orange-800">🎯 Code Promo</h3>
                <p className="text-sm text-orange-600">
                  Avez-vous un code de réduction ? Entrez-le pour économiser !
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Ex: BIENVENUE20"
                    value={promoCode}
                    onChange={handlePromoCodeChange}
                    className="flex-1 px-3 py-2 border border-orange-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-center font-mono uppercase"
                    maxLength={20}
                  />
                  <Button
                    onClick={() => checkPromoForSelectedPlan()}
                    disabled={!promoCode || validatingPromo || !selectedPlan}
                    variant="outline"
                    className="border-orange-300 text-orange-600 hover:bg-orange-50"
                  >
                    {validatingPromo ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
                    ) : (
                      'Valider'
                    )}
                  </Button>
                </div>
                
                {promoValidation && (
                  <div className={`p-3 rounded-md text-sm ${
                    promoValidation.is_valid 
                      ? 'bg-green-50 text-green-800 border border-green-200' 
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}>
                    <div className="flex items-center">
                      {promoValidation.is_valid ? (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      ) : (
                        <X className="w-4 h-4 mr-2" />
                      )}
                      <span>{promoValidation.message}</span>
                    </div>
                    {promoValidation.is_valid && promoValidation.final_price !== undefined && (
                      <div className="mt-2 font-semibold">
                        Prix final: {promoValidation.final_price}€ 
                        {selectedPlan && (
                          <span className="text-green-600 ml-2">
                            (économie: {(
                              (selectedBilling === 'yearly' ? selectedPlan.price_yearly : selectedPlan.price_monthly) - 
                              promoValidation.final_price
                            ).toFixed(2)}€)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="mt-4 text-xs text-orange-600 text-center">
                💡 Codes disponibles: BIENVENUE20, FIRST50, PREMIUM30
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Plans grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              currentPlan={currentSubscription}
              onSelectPlan={handleSelectPlan}
              isPopular={index === 1} // Make the middle plan (Standard) popular
            />
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-center mb-8">Questions Fréquentes</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h4 className="font-semibold mb-2">Puis-je changer de plan à tout moment ?</h4>
                <p className="text-sm text-gray-600">
                  Oui, vous pouvez upgrader ou downgrader votre plan à tout moment. 
                  Les changements prennent effet immédiatement.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h4 className="font-semibold mb-2">Comment sont calculées les commissions ?</h4>
                <p className="text-sm text-gray-600">
                  Les commissions sont prélevées automatiquement sur chaque réservation payée par vos clients, 
                  selon le taux de votre plan d'abonnement.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h4 className="font-semibold mb-2">Que se passe-t-il si j'annule ?</h4>
                <p className="text-sm text-gray-600">
                  Vous revenez automatiquement au plan gratuit avec 20% de commission. 
                  Vos ateliers restent actifs.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h4 className="font-semibold mb-2">Y a-t-il des frais cachés ?</h4>
                <p className="text-sm text-gray-600">
                  Non, les prix affichés sont TTC. Seules les commissions sur vos ventes 
                  s'appliquent selon votre plan.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChefSubscriptionPlans;